import { __extends } from "tslib";
import { observable, computed, intercept, reaction, makeObservable, action } from 'mobx';
import { List, Misc, Validation, Utils } from '@singularsystems/neo-core';
import { ModelCreatorBase } from '@singularsystems/neo-core/dist/Model/ModelCreatorBase';
import PropertyInstance from '@singularsystems/neo-core/dist/Model/PropertyInstance';
var ReactModelCreator = /** @class */ (function (_super) {
    __extends(ReactModelCreator, _super);
    function ReactModelCreator() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    ReactModelCreator.prototype.convertInstance = function (instance, options) {
        if (this.typeInfo.inheritsBindableBase) {
            this.setupModel(this.typeInfo, instance, options);
        }
        else {
            this.setupObject(this.typeInfo, instance, options);
        }
        return instance;
    };
    ReactModelCreator.prototype.setupModel = function (typeInfo, model, options) {
        if (this.typeInfo.inheritsModelBase) {
            model.validator = new Validation.ModelValidator(model, typeInfo.objectRules);
        }
        model.meta = {};
        model.isSuspended = true;
        this.setupObject(typeInfo, model, options);
        model.isSuspended = false;
    };
    ReactModelCreator.prototype.setupObject = function (typeInfo, object, options) {
        var _a, _b;
        var annotations = {};
        var observables = {};
        for (var _i = 0, _c = typeInfo.properties; _i < _c.length; _i++) {
            var propertyInfo = _c[_i];
            if (propertyInfo.isObservable !== false) {
                var propertyName = propertyInfo.propertyName, makeObservable_1 = (_a = propertyInfo.isObservable) !== null && _a !== void 0 ? _a : options.makeObservable;
                var propertyInstance = void 0;
                if (this.typeInfo.inheritsBindableBase) {
                    var model = object;
                    propertyInstance = new PropertyInstance(model, propertyInfo, makeObservable_1);
                    model.meta[propertyName] = propertyInstance;
                    // Rules
                    if (propertyInfo.rules.length > 0 && model.validator) {
                        model.validator.addProperty(propertyInstance);
                    }
                }
                if (propertyInfo.isComputed !== null) {
                    if (propertyInfo.descriptor && propertyInfo.isComputed) {
                        annotations[propertyName] = computed({ keepAlive: true });
                    }
                }
                else if (makeObservable_1) {
                    var property = { propertyInfo: propertyInfo, propertyInstance: propertyInstance };
                    observables[propertyName] = property;
                    this.setMobxObservableType(object, propertyName, property, annotations);
                }
            }
        }
        for (var _d = 0, _e = Array.from(typeInfo.methods.values()); _d < _e.length; _d++) {
            var methodInfo = _e[_d];
            if ((_b = methodInfo.isAction) !== null && _b !== void 0 ? _b : options.autoActions) {
                annotations[methodInfo.name] = action;
            }
        }
        // Run mobx's setup for this instance.
        makeObservable(object, annotations, { proxy: false });
        // Setup once properties have been converted to observables.
        for (var propertyKey in observables) {
            var property = observables[propertyKey];
            this.addChangeTracking(object, propertyKey, property);
            if (property.convertList) {
                this.setupList(object, propertyKey, property.convertList);
            }
        }
    };
    ReactModelCreator.prototype.setMobxObservableType = function (object, propertyName, property, annotations) {
        var propertyValue = object[propertyName], propertyInfo = property.propertyInfo;
        this.ensureChildInfo(object, propertyInfo);
        if (propertyInfo.relationType === Misc.RelationType.ChildList) {
            annotations[propertyName] = observable.shallow;
            property.convertList = propertyValue;
        }
        else if (propertyValue instanceof Array) {
            annotations[propertyName] = observable.shallow;
        }
        else {
            annotations[propertyName] = observable.ref;
        }
    };
    ReactModelCreator.prototype.addChangeTracking = function (object, propertyName, property) {
        var _this = this;
        var propertyInfo = property.propertyInfo, propertyInstance = property.propertyInstance;
        var trackProperty = this.typeInfo.inheritsModelBase && !propertyInfo.noTrack && propertyInfo.relationType !== Misc.RelationType.ChildList;
        var onChangedOptions = propertyInfo && propertyInfo.onChangedOptions;
        if (onChangedOptions && onChangedOptions.requiresOldValue && !propertyInstance) {
            throw Error("Cannot use previous value for OnChanged on property: ".concat(propertyName, ". Model does not extend BindableBase."));
        }
        if (propertyInstance && (trackProperty || propertyInfo.propertyType === Misc.DataType.Date || (onChangedOptions && onChangedOptions.requiresOldValue))) {
            intercept(object, propertyName, function (onChange) {
                var currentValue = object[propertyName];
                if (!object.isSuspended) {
                    if (trackProperty && onChange.newValue !== currentValue)
                        object.isSelfDirty = true;
                    if (propertyInfo.propertyType === Misc.DataType.Date) {
                        if (typeof onChange.newValue === "string") {
                            throw "Error: tried to assign a string to a date property.";
                        }
                    }
                }
                propertyInstance.previousValue = currentValue;
                return onChange;
            });
        }
        var onChanged;
        if (onChangedOptions && onChangedOptions.onChangedFn) {
            if (propertyInstance && onChangedOptions.delayMs) {
                onChanged = function (oldValue, newValue) {
                    Utils.debounce(propertyInstance, function (state) { return onChangedOptions.onChangedFn.call(object, state[0], newValue); }, onChangedOptions.delayMs, oldValue);
                };
            }
            else {
                onChanged = onChangedOptions.onChangedFn.bind(object);
            }
        }
        if (onChanged) {
            var newValue_1;
            reaction(function () {
                newValue_1 = object[propertyName];
                return newValue_1;
            }, function () {
                if (!_this.typeInfo.inheritsBindableBase || !object.isSuspended) {
                    onChanged.call(object, propertyInstance === null || propertyInstance === void 0 ? void 0 : propertyInstance.previousValue, newValue_1);
                }
            });
        }
    };
    ReactModelCreator.prototype.setupList = function (object, propertyName, originalList) {
        var obsArray = object[propertyName];
        List.copyMethods(obsArray);
        originalList.setupProxy(obsArray, object);
        // Don't allow list to change, just change the list contents.
        intercept(object, propertyName, function (onChange) {
            var newValue = onChange.newValue, currentValue = object[propertyName];
            if (!newValue) {
                currentValue.length = 0;
            }
            else {
                if (newValue.childObjectType && newValue.setParent) {
                    // Cannot re-assign this property, so we have to clear the array, and copy the items from the source array.
                    Utils.batchUpdate(function () {
                        currentValue.length = 0;
                        currentValue.push.apply(currentValue, newValue);
                    });
                }
                else {
                    throw "Do not assign a plain array to a List property. Either call ".concat(propertyName, ".set(...) or ").concat(propertyName, ".update(...)");
                }
            }
            // Instruct mobx to ignore the set.
            return null;
        });
    };
    return ReactModelCreator;
}(ModelCreatorBase));
export { ReactModelCreator };
